import React, { useRef } from "react";
import { Box, Button, Typography, CircularProgress, Tooltip } from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  InfoOutlined as InfoIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

// Types
import { ImageAsset } from "../types/MeGenTypes";

type ImageAssetsPanelProps = {
  assets: ImageAsset[];
  /** filename[] ที่เลือกอยู่ของ scene นี้ */
  selected: string[];
  onToggle: (filename: string) => void;
  onUpload: (file: File) => void;
  onDelete?: (filename: string) => void;
  deletingFilename?: string | null;
  buildImageUrl?: (url: string) => string;
  isLoading?: boolean;
  isUploading?: boolean;
  disabled?: boolean;
  maxSelected?: number;
};

// Consts
const MAX_SELECTED = 3;

const ImageAssetsPanel: React.FC<ImageAssetsPanelProps> = (props) => {
  const maxSelected = props?.maxSelected || MAX_SELECTED;

  const fileInputRef = useRef<HTMLInputElement>(null);

  const isMaxReached = props.selected.length >= maxSelected;

  const handleSelectFile = (file: File | null | undefined) => {
    if (!file || props.disabled) return;
    if (!file.type.startsWith("image/")) return;
    props.onUpload(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <Box
      id="megen-image-assets-root"
      sx={{ height: "100%", minWidth: 0, display: "flex", flexDirection: "column" }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5, flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: "#db2777" }}>
            Image Assets
          </Typography>
          <Box
            sx={{
              fontSize: "0.65rem",
              fontWeight: 700,
              px: 0.75,
              py: 0.25,
              borderRadius: "9999px",
              backgroundColor: isMaxReached ? "#fef2f2" : "#fce7f3",
              color: isMaxReached ? "#dc2626" : "#db2777",
              border: `1px solid ${isMaxReached ? "#fecaca" : "#f9a8d4"}`,
            }}
          >
            {props.selected.length}/{maxSelected}
          </Box>

          <Tooltip
            arrow
            placement="top"
            title={
              <Box sx={{ fontSize: "0.7rem", lineHeight: 1.6, py: 0.25 }}>
                ตัวเลขบนรูปคือลำดับที่ใช้อ้างอิงใน Scene Description
                <br />
                (เช่น ① = [Image 1]) — เรียงตามลำดับที่คลิกเลือก
              </Box>
            }
          >
            <InfoIcon
              id="megen-image-assets-order-info"
              sx={{
                fontSize: 14,
                color: "#94a3b8",
                "&:hover": { color: "#db2777" },
              }}
            />
          </Tooltip>
        </Box>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => handleSelectFile(e.target.files?.[0])}
        />
        <Button
          id="megen-image-assets-upload-btn"
          size="small"
          startIcon={
            props?.isUploading ? (
              <CircularProgress size={12} sx={{ color: "white" }} />
            ) : (
              <CloudUploadIcon sx={{ fontSize: 14 }} />
            )
          }
          onClick={() => fileInputRef.current?.click()}
          disabled={props?.disabled || props?.isUploading}
          sx={{
            textTransform: "none",
            fontSize: "0.7rem",
            fontWeight: 600,
            color: "white",
            backgroundColor: "#f382a0",
            borderRadius: "9999px",
            py: 0.375,
            px: 1.5,
            boxShadow: "none",
            "&:hover": { backgroundColor: "#e91e63", boxShadow: "none" },
            "&:disabled": { backgroundColor: "#f9a8d4", color: "white" },
          }}
        >
          {props?.isUploading ? "Uploading..." : "Upload Image Asset"}
        </Button>
      </Box>

      {/* Assets strip */}
      <Box
        id="megen-image-assets-strip"
        sx={{
          flex: 1,
          minWidth: 0,
          border: "1px solid #e2e8f0",
          borderRadius: "0.75rem",
          backgroundColor: "#fffafc",
          p: 1.5,
          minHeight: 118,
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
        }}
      >
        {props?.isLoading ? (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, width: "100%" }}>
            <CircularProgress size={16} sx={{ color: "#db2777" }} />
            <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>Loading assets...</Typography>
          </Box>
        ) : props.assets.length === 0 ? (
          <Box sx={{ width: "100%", textAlign: "center" }}>
            <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>
              ยังไม่มี Image Asset — กด "Upload Image Asset" เพื่อเพิ่มรูป
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              overflowX: "auto",
              width: "100%",
              minWidth: 0,
              pb: 0.5,
              "&::-webkit-scrollbar": { height: 6 },
              // "&::-webkit-scrollbar-thumb": { backgroundColor: "#f9a8d4", borderRadius: 3 },
              "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
            }}
          >
            {props.assets.map((asset) => {
              const selectedIndex = props.selected.indexOf(asset.filename);
              const isSelected = selectedIndex !== -1;
              // ลำดับอ้างอิงใน prompt = ตำแหน่งใน reference_images (เรียงตามลำดับที่คลิก)
              const refNumber = selectedIndex + 1;
              // เลือกครบแล้ว → รูปที่ยังไม่ถูกเลือกจะกดไม่ได้ (แต่ยังเอาออกได้)
              const isBlocked = !isSelected && isMaxReached;
              const isDeleting = props?.deletingFilename === asset.filename;
              const isClickable = !props?.disabled && !isBlocked && !isDeleting;

              return (
                <Box
                  id={`megen-image-asset-${asset.filename}`}
                  key={asset.filename}
                  onClick={() => isClickable && props.onToggle(asset.filename)}
                  title={asset.original_name}
                  sx={{
                    flexShrink: 0,
                    width: 104,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.5,
                    cursor: isClickable ? "pointer" : "not-allowed",
                    opacity: isBlocked || isDeleting ? 0.4 : 1,
                    "&:hover .megen-asset-delete-btn": { opacity: 1 },
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: 100,
                      height: 100,
                      borderRadius: "0.5rem",
                      overflow: "hidden",
                      backgroundColor: "#fce7f3",
                      border: isSelected ? "2.5px solid #db2777" : "1.5px solid #e2e8f0",
                      boxShadow: isSelected ? "0 0 0 3px rgba(219,39,119,0.15)" : "none",
                      transition: "all 0.15s",
                      "&:hover": isClickable ? { borderColor: "#f9a8d4" } : {},
                    }}
                  >
                    <Box
                      component="img"
                      src={props?.buildImageUrl ? props.buildImageUrl(asset.url) : asset.url}
                      alt={asset.original_name}
                      sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />

                    {/* ลำดับอ้างอิง = [Image N] ใน Scene Description */}
                    {isSelected && (
                      <Box
                        id={`megen-image-asset-order-${asset.filename}`}
                        sx={{
                          position: "absolute",
                          top: 4,
                          left: 4,
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          backgroundColor: "#db2777",
                          color: "white",
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          lineHeight: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "1.5px solid white",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
                        }}
                      >
                        {refNumber}
                      </Box>
                    )}

                    {/* ปุ่มลบ asset (ขวาบน) */}
                    {props?.onDelete && !props?.disabled && (
                      <Box
                        id={`megen-image-asset-delete-${asset.filename}`}
                        className="megen-asset-delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isDeleting) return;
                          props.onDelete?.(asset.filename);
                        }}
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          backgroundColor: "rgba(15, 23, 42, 0.55)",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: isDeleting ? "not-allowed" : "pointer",
                          opacity: 0,
                          transition: "opacity 0.15s, background-color 0.15s",
                          "&:hover": { backgroundColor: "#dc2626" },
                        }}
                      >
                        {isDeleting ? (
                          <CircularProgress size={10} sx={{ color: "white" }} />
                        ) : (
                          <CloseIcon sx={{ fontSize: 13 }} />
                        )}
                      </Box>
                    )}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: isSelected ? 700 : 500,
                      color: isSelected ? "#db2777" : "#94a3b8",
                      width: "100%",
                      textAlign: "center",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {asset.original_name}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <Typography sx={{ fontSize: "0.7rem", color: "#94a3b8", mt: 0.5, flexShrink: 0 }}>
        คลิกเพื่อเลือกรูปอ้างอิง — คลิกซ้ำที่รูปที่เลือกอยู่เพื่อยกเลิก
      </Typography>
    </Box>
  );
};

export default ImageAssetsPanel;
